import React, {
  useState,
  createContext,
  useEffect,
  useContext,
  useCallback,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { emptyPage, isJsonString, trimPageData } from "./../helpers";
import { APIProviderContext } from "../api";
import { toast } from "react-toastify";
import useInterval from "use-interval";
import _ from "lodash";
import Toast from "../toast/toast";
import { CorrectIcon } from "../../assets/ui-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { updatePageWithComponents } from "./component-blocks";
import { optimizeNodeTree } from "./optimize-node-tree";
import { usePrompt, useScript } from "../hooks";
import { useContextMenu } from "react-contexify";
import { RealtimeContext } from "../realtime";
import { hasAccessNoHooks } from "../access";
import { AccountContext } from "../account/account";
import { GrigoraDashboardContext } from "../dashboard/context";

const CONTEXT_MENU_ID = "context-menu";

const GrigoraEditorContext = createContext();

function GrigoraEditorProvider(props) {
  const { userAttributes } = useContext(AccountContext) ?? {};
  const {
    project: projectFromDashboard,
    setCMSPosts,
    domain,
  } = useContext(GrigoraDashboardContext) ?? {};
  const {
    getPageContent,
    publishQueueCheck,
    projectPagesData,
    projectData,
    projectEditorLoad,
    projectComponents: projectComponentsAPI,
    publishProject: publishProjectAPI,
    addNewPage: addNewPageAPI,
    updatePages: updatePagesAPI,
    updateComponents: updateComponentsAPI,
    updateProjectWebclip: updateProjectWebclipAPI,
    updateProjectFavicon: updateProjectFaviconAPI,
    updateProjectSitelogo: updateProjectSitelogoAPI,
    updateProjectData: updateProjectDataAPI,
    deletePage: deletePageAPI,
    duplicatePage: duplicatePageAPI,
    updatePageData: updatePageDataAPI,
    getCMSPosts,
  } = useContext(APIProviderContext) ?? {};
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingBlocks, setIsAddingBlocks] = useState(false);
  const [isAddingSections, setIsAddingSections] = useState(false);
  const [isLayersVisible, setIsLayersVisible] = useState(false);
  const { projectID } = useParams();
  const { state: routerState } = useLocation();
  const [failedLoading, setFailedLoading] = useState(false);
  const [project, setProjectData] = useState(null);
  const [pagesData, setPagesData] = useState(null);
  const [projectComponents, setProjectComponents] = useState(null);
  const [pageContents, setPagesContents] = useState(null);
  const [pageContentsCheckpoint, setPagesContentsCheckpoint] = useState(null);
  const [currentPage, setCurrentPage] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [leftWidget, setLeftWidget] = useState("elements");
  const [publishQueueID, setPublishQueueID] = useState("");
  const [isNewProjectPublished, setIsNewProjectPublished] = useState(false);
  const [prevPage, setPrevPage] = useState(null);
  const { socket, socketEvents } = useContext(RealtimeContext) ?? {};

  // State to manage home menu
  const [isHomeMenuOpen, setIsHomeMenuOpen] = useState(false);

  // State to manage publish menu
  const [isPublishMenuOpen, setIsPublishMenuOpen] = useState(false);

  const [isNewPageContentLoading, setIsNewPageContentLoading] = useState(false);
  const [hasShownBlockSettingFirstTime, setHasShownBlockSettingFirstTime] =
    useState(false);
  const [isBlockSettingsVisible, setIsBlockSettingsVisible] = useState(false);
  const [failedLoadingPageContents, setFailedLoadingPageContents] = useState(
    {},
  );
  const [displaySize, setDisplaySize] = useState("desktop");
  // const [concurrentTasks, setConcurrentTasks] = useState([]);
  const [publishedCheckCounts, setPublishedCheckCounts] = useState(0);
  const [keyboardShortcutModal, setKeyboardShortcutModal] = useState(false);

  const { show: showContextMenu, hideAll: hideContextMenu } = useContextMenu({
    id: CONTEXT_MENU_ID,
  });

  const navigate = useNavigate();

  function handleContextMenu(event) {
    // Get Iframe offsets
    const frameOffset =
      event?.target?.ownerDocument?.defaultView?.frameElement?.getBoundingClientRect() ??
      {};
    const { top: frameTop = 0, left: frameLeft = 0 } = frameOffset
      ? frameOffset
      : { frameTop: 0, frameLeft: 0 };

    showContextMenu({
      event,
      position: {
        x: event.clientX + frameLeft + 5,
        y: event.clientY + frameTop - 15,
      },
      props: {
        key: "value",
      },
    });
  }

  // // Using useInterval instead of setInterval + useEffect
  // useInterval(() => {
  //   runConcurrentTask();
  // }, 2000);

  var { gsap } = useScript(
    "https://cdnjs.cloudflare.com/ajax/libs/gsap/3.11.5/gsap.min.js",
    "gsap",
  );

  const resetStates = () => {
    setIsLoading(true);
    setFailedLoading(false);
    setPagesData(null);
    setCurrentPage(null);
    setPagesContents(null);
    setProjectComponents(null);
    setPagesContentsCheckpoint(null);
    setIsPublishing(false);
    // setConcurrentTasks([]);
  };

  const projectLoaded = () => {
    setIsLoading(false);
    setFailedLoading(false);
  };

  // Note to self: Check if we can useMemo
  const parsePages = (pages) => {
    var pagesObj = {};
    if (Array.isArray(pages) && pages.length > 0) {
      pages.forEach((page) => {
        pagesObj[trimPageData(page.sk)] = { ...page };
      });
    }
    return pagesObj;
  };

  // Note to self: Check if we can useMemo
  const decideOpenedPage = (pages, componentsData) => {
    var selected = undefined;

    if (Array.isArray(pages) && pages.length > 0) {
      pages.forEach((page) => {
        if (page.slug === "") {
          selected = trimPageData(page.sk);
        }
      });
      if (selected === undefined) {
        selected = trimPageData(pages[0].sk);
      }
    }
    if (selected !== undefined) {
      setCurrentPage(selected);
      getPageContent({ project_id: projectID, page_id: selected })
        .then((data) => {
          // parse page contents
          if (isJsonString(data.content)) {
            const parsedContent = JSON.parse(data.content);

            const cmsBlocks = [
              "CMS",
              "PostDate",
              "Text",
              "PostCategories",
              "PostTimeToRead",
              "PostTitle",
              "PostFeaturedImage",
              "PostContent",
              "PostExcerpt",
              "Container",
              "Button",
            ];
            // check if page contents has cms blocks
            const hasCMSBlocks = Object.keys(parsedContent).some((child) => {
              if (
                parsedContent[child]?.name === "Text" &&
                parsedContent[child]?.props?.isContentDynamic
              ) {
                return true;
              }
              return cmsBlocks.includes(parsedContent[child]?.name);
            });

            // fetch cms posts if page contents has cms blocks
            if (hasCMSBlocks) {
              getCMSPosts(projectID)
                .then((res) => {
                  res = res?.filter((item) => {
                    return item.hasOwnProperty("first_published");

                    // filter by access
                    // return hasAccessNoHooks(
                    //   projectFromDashboard,
                    //   userAttributes?.sub,
                    //   [0, 4000, 4001],
                    //   item.author === userAttributes?.sub
                    //     ? hasAccessNoHooks(
                    //         projectFromDashboard,
                    //         userAttributes?.sub,
                    //         [4002],
                    //       )
                    //     : false,
                    // );
                  });
                  setCMSPosts(res);
                })
                .catch((err) => {
                  console.log(err);
                });
            }
          }
          setPagesContents({
            ...pageContents,
            [selected]: data.content,
          });
          setPagesContentsCheckpoint({
            ...pageContents,
            [selected]: data.content,
          });
          projectLoaded();
        })
        .catch((err) => {
          setPagesContents({ ...pageContents, [selected]: "failed" });
          setPagesContents({ ...pageContents, [selected]: "failed" });
          projectLoaded();
        });
    }
  };

  function shouldClose() {
    if (_.isEqual(pageContents, pageContentsCheckpoint)) {
      return true;
    }
    return false;
  }

  function preventClose(e) {
    if (shouldClose()) {
      return;
    }
    e.preventDefault();
    e.returnValue = "";
  }

  function componentArrToObj(arr) {
    var obj = {};
    arr.map((item) => {
      obj[item.sk] = item;
    });
    return obj;
  }

  const socketHandler = useCallback(
    (event) => {
      var queueID = event?.data?.queueID;

      if (publishQueueID) {
        if (publishQueueID === queueID) {
          setProjectData({
            ...project,
            status: "published",
            last_published: Math.floor(new Date().getTime() / 1000),
          });
          setIsPublishing(false);
          setPublishQueueID("");
          if (routerState?.isFirstTime) {
            toast.update("publishing", {
              render: (
                <>
                  <span>
                    Website Published.{" "}
                    <a
                      target={"_blank"}
                      className="text-ui-dark-blue"
                      href={`https://${domain}`}
                      rel="noreferrer"
                    >
                      View.
                    </a>
                  </span>
                </>
              ),
              icon: <CorrectIcon />,
              type: "success",
              isLoading: false,
              autoClose: 500,
            });
            setIsNewProjectPublished(true);
          } else {
            toast.success(
              <>
                <span>
                  Website Published.{" "}
                  <a
                    target={"_blank"}
                    className="text-ui-dark-blue"
                    href={`https://${domain}`}
                    rel="noreferrer"
                  >
                    View.
                  </a>
                </span>
              </>,
              {
                icon: <CorrectIcon />,
              },
            );
          }
        }
      }
    },
    [
      publishQueueID,
      project,
      setProjectData,
      setIsPublishing,
      setPublishQueueID,
      routerState,
    ],
  );

  useEffect(() => {
    // Listen for messages
    if (socketEvents) {
      socketEvents.subscribe("PUBLISH_PROJECT", socketHandler);
    }

    return () => {
      if (socketEvents) {
        socketEvents.unsubscribe("PUBLISH_PROJECT", socketHandler);
      }
    };
  }, [socket, socketHandler]);

  usePrompt("Changes not saved. Are you sure want to exit?", shouldClose());

  useEffect(() => {
    resetStates();

    projectEditorLoad?.({ id: projectID })
      .then((data) => {
        setProjectData(data.project);
        setProjectComponents(componentArrToObj(data.components));
        setPagesData(parsePages(data.pages));
        decideOpenedPage(data.pages, data.components);
      })
      .catch((err) => {
        console.log(err);
        resetStates();
        setFailedLoading(true);
      });

    window.addEventListener("beforeunload", preventClose);

    return () => {
      resetStates();
      window.removeEventListener("beforeunload", preventClose);
    };
  }, []);

  useEffect(() => {
    window.addEventListener("beforeunload", preventClose);
    return () => {
      window.removeEventListener("beforeunload", preventClose);
    };
  }, [pageContents, pageContentsCheckpoint]);

  function changeCurrentPage(id) {
    setIsNewPageContentLoading(true);

    // If page is already fetched
    if (id in pageContents && pageContents[id]) {
      setCurrentPage(id);
      setIsNewPageContentLoading(false);
      setPagesContents({ ...pageContents });
      return;
    }

    // If page is not fetched
    getPageContent({ project_id: projectID, page_id: id })
      .then((data) => {
        // Resetting failed loading page contents to false since the page has loaded
        setFailedLoadingPageContents({
          ...failedLoadingPageContents,
          [id]: false,
        });

        setPagesContents({ ...pageContents, [id]: data.content });
        setPagesContentsCheckpoint({
          ...pageContentsCheckpoint,
          [id]: data.content,
        });

        setCurrentPage(id);
        setIsNewPageContentLoading(false);
      })
      .catch((err) => {
        // Setting failed loading page contents to true since the page has failed to load
        setFailedLoadingPageContents({
          ...failedLoadingPageContents,
          [id]: true,
        });

        setPagesContents({ ...pageContents, [id]: "" });
        setPagesContentsCheckpoint({
          ...pageContentsCheckpoint,
          [id]: "",
        });
        setCurrentPage(id);
        setIsNewPageContentLoading(false);
      });
  }

  const changeProjectData = async (name, slug) => {
    return await new Promise((resolve, reject) => {
      updateProjectDataAPI({ project_id: projectID, name, slug })
        .then((res) => {
          setProjectData({
            ...project,
            name: name ? name : project.name,
            slug: slug ? slug : project.slug,
          });
          toast.success(<Toast title={res.data.Output.message} />, {
            icon: <CorrectIcon />,
          });
          resolve();
        })
        .catch((err) => {
          toast.error(
            <Toast
              title={"Error"}
              description={err?.response?.data?.Output?.message}
            />,
          );
          reject();
        });
    });
  };

  const publishProject = async (query) => {
    return await new Promise((resolve, reject) => {
      if (isPublishing) {
        return;
      }

      if (routerState?.isFirstTime) {
        toast.loading("Publishing...", {
          toastId: "publishing",
        });
      }

      setIsPublishing(true);

      updateProjectDataAPI({
        project_id: projectID,
        ...{
          // Only update required fields
          name: project?.name ? project.name : "Website Name",
          typography: project?.typography ?? "empty",
          fonts: project?.fonts ?? "empty",
        },
      })
        .then((data) => {
          updateComponentsAPI(projectID, projectComponents).then((res) => {
            // Update pages with components
            var pageContentsUpdated = {
              ...pageContents,
            };

            Object.keys(pageContents).map((pageID) => {
              pageContentsUpdated[pageID] = updatePageWithComponents(
                pageContents[pageID],
                projectComponents,
                query,
              );

              pageContentsUpdated[pageID] = optimizeNodeTree(
                pageContentsUpdated[pageID],
              );
            });

            updatePagesAPI({
              project_id: projectID,
              pages_data: pagesData,
              pages_contents: pageContentsUpdated,
            })
              .then((res) => {
                // toast.success(<Toast title={res.data.Output.message} />, {
                //   icon: <CorrectIcon />,
                // });
                if (res.data.Output.currentTime) {
                  setProjectData({
                    ...project,
                    last_update: res.data.Output.currentTime,
                  });
                }
                setPagesContents(pageContentsUpdated);
                setPagesContentsCheckpoint(pageContentsUpdated);
                publishProjectAPI({ id: projectID })
                  .then((res) => {
                    setPublishQueueID(res.data.Output.queueID);
                    // addConcurrentTask("publishedCheck");
                  })
                  .catch((err) => {
                    console.log(err);
                    setIsPublishing(false);
                    if (routerState?.isFirstTime) {
                      toast.update("publishing", {
                        render: (
                          <Toast
                            title={"Error"}
                            description={err?.response?.data?.Output?.message}
                          />
                        ),
                        type: "error",
                        isLoading: false,
                        autoClose: 2000,
                      });
                    } else {
                      toast.error(
                        <Toast
                          title={"Error"}
                          description={err?.response?.data?.Output?.message}
                        />,
                      );
                    }
                  });
              })
              .catch((err) => {
                if (routerState?.isFirstTime) {
                  toast.update("publishing", {
                    render: (
                      <Toast
                        title={"Error"}
                        description={err?.response?.data?.Output?.message}
                      />
                    ),
                    type: "error",
                    isLoading: false,
                    autoClose: 2000,
                  });
                } else {
                  toast.error(
                    <Toast
                      title={"Error"}
                      description={err?.response?.data?.Output?.message}
                    />,
                  );
                }
                setIsPublishing(false);
                reject();
              });
          });
        })
        .catch((err) => {
          if (routerState?.isFirstTime) {
            toast.update("publishing", {
              render: (
                <Toast
                  title={"Error"}
                  description={err?.response?.data?.Output?.message}
                />
              ),
              type: "error",
              isLoading: false,
              autoClose: 2000,
            });
          } else {
            toast.error(
              <Toast
                title={"Error"}
                description={err?.response?.data?.Output?.message}
              />,
            );
          }
          setIsPublishing(false);
          reject();
        });
    });
  };

  const addNewPage = async (props) => {
    return await new Promise((resolve, reject) => {
      addNewPageAPI({ ...props, project_id: projectID })
        .then((res) => {
          setPagesData({
            ...pagesData,
            [res.data.Output.id]: { ...props, content: undefined },
          });
          setPagesContents({
            ...pageContents,
            [res.data.Output.id]: props.content ? props.content : emptyPage(),
          });
          setPagesContentsCheckpoint({
            ...pageContentsCheckpoint,
            [res.data.Output.id]: props.content ? props.content : emptyPage(),
          });
          setCurrentPage(res.data.Output.id);
          toast.success(<Toast title={res.data.Output.message} />, {
            icon: <CorrectIcon />,
          });
          resolve();
        })
        .catch((err) => {
          toast.error(
            <Toast
              title={"Error"}
              description={err?.response?.data?.Output?.message}
            />,
          );
          reject();
        });
    });
  };

  const updatePages = async (query) => {
    return await new Promise((resolve, reject) => {
      if (isPublishing) {
        return;
      }
      setIsPublishing(true);
      const toastID = toast.loading("Saving...");
      updateProjectDataAPI({
        project_id: projectID,
        ...{
          // Only update required fields
          name: project?.name ? project.name : "Website Name",
          typography: project?.typography ?? "empty",
          fonts: project?.fonts ?? "empty",
        },
      })
        .then((data) => {
          updateComponentsAPI(projectID, projectComponents)
            .then((res) => {
              // Update pages with components
              var pageContentsUpdated = {
                ...pageContents,
              };

              Object.keys(pageContents).map((pageID) => {
                pageContentsUpdated[pageID] = updatePageWithComponents(
                  pageContents[pageID],
                  projectComponents,
                  query,
                );

                // Optimize Node Tree
                pageContentsUpdated[pageID] = optimizeNodeTree(
                  pageContentsUpdated[pageID],
                );
              });

              updatePagesAPI({
                project_id: projectID,
                pages_data: pagesData,
                pages_contents: pageContentsUpdated,
              })
                .then((res) => {
                  toast.update(toastID, {
                    render: <Toast title={res.data.Output.message} />,
                    icon: <CorrectIcon />,
                    type: "success",
                    isLoading: false,
                    autoClose: 2000,
                  });

                  // toast.success(<Toast title={res.data.Output.message} />, {
                  //   icon: <CorrectIcon />,
                  // });
                  if (res.data.Output.currentTime) {
                    setProjectData({
                      ...project,
                      last_update: res.data.Output.currentTime,
                    });
                  }
                  setPagesContents(pageContentsUpdated);
                  setPagesContentsCheckpoint(pageContentsUpdated);
                  setIsPublishing(false);
                  resolve();
                })
                .catch((err) => {
                  toast.update(toastID, {
                    render: (
                      <Toast
                        title={"Error"}
                        description={err?.response?.data?.Output?.message}
                      />
                    ),
                    type: "error",
                    isLoading: false,
                    autoClose: 2000,
                  });
                  setIsPublishing(false);
                  reject();
                });
            })
            .catch((err) => {
              toast.update(toastID, {
                render: (
                  <Toast
                    title={"Error"}
                    description={err?.response?.data?.Output?.message}
                  />
                ),
                type: "error",
                isLoading: false,
                autoClose: 2000,
              });
              setIsPublishing(false);
              reject();
            });
        })
        .catch((err) => {
          console.log(err);
          toast.update(toastID, {
            render: (
              <Toast
                title={"Error"}
                description={err?.response?.data?.Output?.message}
              />
            ),
            type: "error",
            isLoading: false,
            autoClose: 2000,
          });
          setIsPublishing(false);
          reject();
        });
    });
  };

  const updateProjectFavicon = async (image) => {
    return await new Promise((resolve, reject) => {
      updateProjectFaviconAPI(projectID, image)
        .then((res) => {
          toast.success(<Toast title={res.data.Output.message} />, {
            icon: <CorrectIcon />,
          });
          setProjectData({
            ...project,
            favicon: res.data.Output.favicon,
          });
          resolve(res.data.Output.favicon);
        })
        .catch((err) => {
          toast.error(
            <Toast
              title={"Error"}
              description={err?.response?.data?.Output?.message}
            />,
          );
          reject();
        });
    });
  };

  const updateProjectSitelogo = async (image) => {
    return await new Promise((resolve, reject) => {
      updateProjectSitelogoAPI(projectID, image)
        .then((res) => {
          toast.success(<Toast title={res.data.Output.message} />, {
            icon: <CorrectIcon />,
          });
          setProjectData({
            ...project,
            sitelogo: res.data.Output.sitelogo,
          });
          resolve(res.data.Output.sitelogo);
        })
        .catch((err) => {
          toast.error(
            <Toast
              title={"Error"}
              description={err?.response?.data?.Output?.message}
            />,
          );
          reject();
        });
    });
  };

  const updateProjectWebclip = async (image) => {
    return await new Promise((resolve, reject) => {
      updateProjectWebclipAPI(projectID, image)
        .then((res) => {
          toast.success(<Toast title={res.data.Output.message} />, {
            icon: <CorrectIcon />,
          });
          setProjectData({
            ...project,
            webclip: res.data.Output.webclip,
          });
          resolve(res.data.Output.webclip);
        })
        .catch((err) => {
          toast.error(
            <Toast
              title={"Error"}
              description={err?.response?.data?.Output?.message}
            />,
          );
          reject();
        });
    });
  };

  function getHomePageID() {
    var key = "";
    Object.keys(pagesData).forEach((item) => {
      if (pagesData[item].slug === "") {
        key = item;
      }
    });
    return key;
  }

  const updatePageData = async (
    pageID,
    name = undefined,
    title = undefined,
    slug = undefined,
    seo_meta_desc = undefined,
    type = undefined,
    template = undefined,
    seo_meta_title = undefined,
  ) => {
    return await new Promise((resolve, reject) => {
      if (!(pageID in pagesData)) {
        reject("Page Doesn't Exists.");
        return;
      }
      updatePageDataAPI(
        projectID,
        pageID,
        name,
        title,
        slug,
        seo_meta_desc,
        type,
        template,
        seo_meta_title,
      )
        .then((res) => {
          var temp = { ...pagesData };
          temp[pageID] = {
            ...temp[pageID],
            name,
            title,
            slug,
            seo_meta_desc,
            type,
            template,
          };
          setPagesData(temp);
          resolve();
        })
        .catch((err) => {
          toast.error(
            <Toast
              title={"Error"}
              description={err?.response?.data?.Output?.message}
            />,
          );
          reject(err);
        });
    });
  };

  const deletePage = async (pageID) => {
    return await new Promise((resolve, reject) => {
      if (!(pageID in pagesData)) {
        reject("Page Doesn't Exists.");
        return;
      }
      // Dont delete home page
      if (pagesData[pageID].slug === "") {
        reject("Cannot delete Home Page.");
        return;
      }
      deletePageAPI(projectID, pageID)
        .then((res) => {
          if (currentPage === pageID) {
            setCurrentPage(getHomePageID());
          }
          var temp = { ...pagesData };
          delete temp[pageID];
          setPagesData(temp);
          var temp = { ...pageContents };
          delete temp[pageID];
          setPagesContents(temp);
          var temp = { ...pageContentsCheckpoint };
          delete temp[pageID];
          setPagesContentsCheckpoint(temp);
          resolve();
        })
        .catch((err) => {
          toast.error(
            <Toast
              title={"Error"}
              description={err?.response?.data?.Output?.message}
            />,
          );
          reject(err);
        });
    });
  };

  const duplicatePage = async (pageID) => {
    return await new Promise((resolve, reject) => {
      duplicatePageAPI(projectID, pageID)
        .then((res) => {
          if (res.data.Output?.data?.id) {
            setPagesData({
              ...pagesData,
              [res.data.Output.data.id]: res.data.Output.data,
            });
            setPagesContents({
              ...pageContents,
              [res.data.Output.data.id]: res.data.Output.data.content,
            });
            setCurrentPage(res.data.Output.data.id);
          }
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const addComponent = async (title, content, id, componentID) => {
    return await new Promise((resolve, reject) => {
      setProjectComponents({
        ...projectComponents,
        ["component-" + componentID]: {
          sk: "component-" + componentID,
          title: title,
          content: content,
          root_node: id,
        },
      });
      resolve(componentID);
    });
  };

  return (
    <GrigoraEditorContext.Provider
      value={{
        isLoading,
        isHomeMenuOpen,
        setIsHomeMenuOpen,
        isPublishMenuOpen,
        setIsPublishMenuOpen,
        projectID,
        pagesData,
        currentPage,
        pageContents,
        pageContentsCheckpoint,
        setPagesContents,
        failedLoading,
        project,
        setProjectData,
        isPublishing,
        publishProject,
        hasShownBlockSettingFirstTime,
        setHasShownBlockSettingFirstTime,
        // concurrentTasks,
        leftWidget,
        setLeftWidget,
        changeCurrentPage,
        addNewPage,
        updatePages,
        isBlockSettingsVisible,
        setIsBlockSettingsVisible,
        isNewPageContentLoading,
        changeProjectData,
        updateProjectFavicon,
        updateProjectWebclip,
        updateProjectSitelogo,
        deletePage,
        updatePageData,
        projectComponents,
        setProjectComponents,
        addComponent,
        displaySize,
        setDisplaySize,
        routerState,
        gsap,
        failedLoadingPageContents,
        setFailedLoadingPageContents,
        showContextMenu,
        hideContextMenu,
        handleContextMenu,
        CONTEXT_MENU_ID,
        isAddingBlocks,
        setIsAddingBlocks,
        isAddingSections,
        setIsAddingSections,
        isLayersVisible,
        setIsLayersVisible,
        keyboardShortcutModal,
        isNewProjectPublished,
        setIsNewProjectPublished,
        setKeyboardShortcutModal,
        prevPage,
        setPrevPage,
        duplicatePage,
      }}
    >
      {props.children}
    </GrigoraEditorContext.Provider>
  );
}

export { GrigoraEditorProvider, GrigoraEditorContext };
